import "@hotwired/turbo-rails"
import { Turbo } from "@hotwired/turbo-rails"
import "../controllers/application"
import "../controllers/index"
import "../offline/index"

// Show progress bar sooner (200ms instead of default 500ms)
Turbo.config.drive.progressBarDelay = 200

// Replace native browser confirm dialogs with DaisyUI modal
Turbo.config.forms.confirm = (message) => {
  const modal = document.getElementById("turbo-confirm-modal")
  if (!modal) return Promise.resolve(window.confirm(message))

  const messageEl = document.getElementById("turbo-confirm-message")
  const okBtn = document.getElementById("turbo-confirm-ok")
  const cancelBtn = document.getElementById("turbo-confirm-cancel")
  const backdrop = document.getElementById("turbo-confirm-backdrop")

  messageEl.textContent = message
  modal.classList.add("modal-open")

  // Focus the cancel button when modal opens
  requestAnimationFrame(() => cancelBtn.focus())

  return new Promise((resolve) => {
    const focusableEls = [cancelBtn, okBtn]

    function cleanup() {
      modal.classList.remove("modal-open")
      okBtn.removeEventListener("click", onConfirm)
      cancelBtn.removeEventListener("click", onCancel)
      backdrop.removeEventListener("click", onCancel)
      document.removeEventListener("keydown", onKeydown)
    }
    function onConfirm() { cleanup(); resolve(true) }
    function onCancel() { cleanup(); resolve(false) }
    function onKeydown(e) {
      if (e.key === "Escape") onCancel()
      if (e.key === "Tab") {
        const first = focusableEls[0]
        const last = focusableEls[focusableEls.length - 1]
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus() }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus() }
        }
      }
    }

    okBtn.addEventListener("click", onConfirm)
    cancelBtn.addEventListener("click", onCancel)
    backdrop.addEventListener("click", onCancel)
    document.addEventListener("keydown", onKeydown)
  })
}

// Suppress .animate-in during Turbo navigation (view transitions handle it)
document.addEventListener("turbo:before-render", () => {
  document.documentElement.classList.add("turbo-navigating")
})
document.addEventListener("turbo:render", () => {
  // Remove after a tick so CSS animation-play-state can react
  requestAnimationFrame(() => {
    document.documentElement.classList.remove("turbo-navigating")
  })
})

// Service worker registration
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
      .then((registration) => {
        // Listen for new service worker versions
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "activated" && navigator.serviceWorker.controller) {
                document.dispatchEvent(new CustomEvent("toast:show", {
                  detail: { message: "New version available — refresh to update", type: "info" }
                }))
              }
            })
          }
        })
      })
      .catch((error) => {
        console.warn("[SW] Registration failed:", error)
      })

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "sync-complete") {
        document.dispatchEvent(new CustomEvent("sync:complete", { detail: event.data }))
      }
      if (event.data?.type === "sync-error") {
        document.dispatchEvent(new CustomEvent("sync:error", { detail: event.data }))
      }
    })
  })
}

// Sync theme from localStorage on every Turbo navigation
// Prevents theme reverting to default when Turbo replaces the page
function syncThemeFromStorage() {
  const saved = localStorage.getItem("logix-theme")
  if (saved) {
    document.documentElement.setAttribute("data-theme", saved)
    // Also sync the theme-color meta tag for mobile browsers
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) {
      meta.content = saved === "dark" ? "#1d232a" : "#ffffff"
    }
  }
}
document.addEventListener("turbo:render", syncThemeFromStorage)
document.addEventListener("turbo:load", syncThemeFromStorage)

// Note: org branding style tag (#org-branding-vars) is replaced naturally by Turbo's
// body swap on each navigation — no manual removal needed (which caused a brief theme flash).
